import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useToast } from '@/hooks/use-toast';
import { Settings, Bell, Shield, Globe, User, CreditCard, Calendar, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EnhancedUserManagementTab from './EnhancedUserManagementTab';
import { ThemeToggle } from '@/components/ThemeToggle';

const UserSettingsTab: React.FC = () => {
  const { user, signOut, userRoles, companyRole, isSuperAdmin, isCompanyAdmin } = useAuth();
  const { subscribed, subscriptionTier, openCustomerPortal } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeSettingsTab, setActiveSettingsTab] = useState('profile');

  // Check if user can manage other users - super_admin or company admin/owner
  const canManageUsers = isSuperAdmin() || isCompanyAdmin();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive"
      });
    }
  };

  const tabsConfig = [
    { value: 'profile', label: 'Profile', icon: User },
    { value: 'billing', label: 'Billing', icon: CreditCard },
    { value: 'system', label: 'System', icon: Settings },
    ...(canManageUsers ? [{ value: 'users', label: 'Users', icon: Users }] : [])
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-black mb-2">Settings</h2>
        <p className="text-muted-foreground">Manage your account settings and preferences.</p>
      </div>

      <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab} className="w-full">
        <TabsList className={`grid w-full grid-cols-${tabsConfig.length}`}>
          {tabsConfig.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-6">
          
          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Update your personal details and contact information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input id="firstName" placeholder="Enter your first name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input id="lastName" placeholder="Enter your last name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={user?.email || ''} 
                  disabled 
                  className="bg-muted"
                />
              </div>
              <Button className="tesla-button">Save Changes</Button>
            </CardContent>
          </Card>

          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Account Information
              </CardTitle>
              <CardDescription>
                View your account details and membership status.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-muted-foreground">Member Since</span>
                <span className="text-sm text-black">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-muted-foreground">Account Status</span>
                <span className="text-sm text-green-600 font-medium">Active</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-muted-foreground">Role</span>
                <span className="text-sm text-black font-medium">
                  {userRoles.includes('super_admin') ? 'Super Admin' :
                   companyRole === 'owner' ? 'Owner' :
                   companyRole === 'admin' ? 'Admin' : 'Member'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-muted-foreground">User ID</span>
                <span className="text-sm text-muted-foreground font-mono">{user?.id}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-6 mt-6">
          
          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription & Billing
              </CardTitle>
              <CardDescription>
                Manage your subscription and billing information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-muted-foreground">Current Plan</span>
                <span className="text-sm text-black font-medium">
                  {subscribed ? (subscriptionTier || 'Premium') : 'Free'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-sm font-medium text-muted-foreground">Status</span>
                <span className={`text-sm font-medium ${subscribed ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {subscribed ? 'Active' : 'Free Plan'}
                </span>
              </div>
              <div className="pt-4 space-y-3">
                {subscribed ? (
                  <Button 
                    onClick={handleManageSubscription}
                    className="w-full"
                  >
                    Manage Subscription
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/pricing')}
                    className="w-full"
                  >
                    Upgrade Plan
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="space-y-6 mt-6">
          
          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
              <CardDescription>
                Configure your notification preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive updates about your assessments</p>
                </div>
                <Switch id="email-notifications" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="marketing-emails">Marketing Emails</Label>
                  <p className="text-sm text-muted-foreground">Receive product updates and offers</p>
                </div>
                <Switch id="marketing-emails" />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="assessment-reminders">Assessment Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get reminded to complete assessments</p>
                </div>
                <Switch id="assessment-reminders" defaultChecked />
              </div>
            </CardContent>
          </Card>

          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Preferences
              </CardTitle>
              <CardDescription>
                Customize your platform experience.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="auto-save">Auto-save Progress</Label>
                  <p className="text-sm text-muted-foreground">Automatically save assessment progress</p>
                </div>
                <Switch id="auto-save" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="detailed-results">Detailed Results</Label>
                  <p className="text-sm text-muted-foreground">Show comprehensive analysis in results</p>
                </div>
                <Switch id="detailed-results" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Appearance</Label>
                  <p className="text-sm text-muted-foreground">Toggle between light and dark mode</p>
                </div>
                <ThemeToggle />
              </div>
            </CardContent>
          </Card>

          <Card className="tesla-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security
              </CardTitle>
              <CardDescription>
                Manage your account security and privacy.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full">
                Change Password
              </Button>
              <Button variant="outline" className="w-full">
                Download My Data
              </Button>
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={handleSignOut}
              >
                Sign out
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {canManageUsers && (
          <TabsContent value="users" className="space-y-6 mt-6">
            <EnhancedUserManagementTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default UserSettingsTab;
